"use client";

import { c as _c } from "react/compiler-runtime";
import { Film, ImagePlus, Loader2, Music, Plus, Sparkles, X } from "lucide-react";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { getVideoDurations, getVideoSizeLabel, getVideoSizes, type VideoDuration, type VideoFrameInputs, type VideoInputImage, type VideoSize } from "@/lib/video-gen";
interface VideoControlsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels: string[];
  videoSize: VideoSize;
  setVideoSize: (value: VideoSize) => void;
  videoDuration: VideoDuration;
  setVideoDuration: (value: VideoDuration) => void;
  audioEnabled: boolean;
  setAudioEnabled: (value: boolean) => void;
  audioToggleDisabled: boolean;
  canUseFrameInputs: boolean;
  canUseReferenceInputs: boolean;
  canUseReferenceVideoInputs: boolean;
  canUseReferenceAudioInputs: boolean;
  frameInputs: VideoFrameInputs;
  setFrameInputs: Dispatch<SetStateAction<VideoFrameInputs>>;
  referenceImages: VideoInputImage[];
  setReferenceImages: Dispatch<SetStateAction<VideoInputImage[]>>;
  referenceVideos: string[];
  setReferenceVideos: Dispatch<SetStateAction<string[]>>;
  referenceAudios: string[];
  setReferenceAudios: Dispatch<SetStateAction<string[]>>;
  supportedVideoSizes: VideoSize[];
  supportedVideoDurations: VideoDuration[];
  isGenerating: boolean;
  onGenerate: () => void;
  imageInputRequired?: boolean;
}
type UploadTarget = "frame-start" | "frame-end" | "reference";
export function VideoControls(t0) {
  const $ = _c(169);
  const {
    prompt,
    setPrompt,
    selectedModels,
    videoSize,
    setVideoSize,
    videoDuration,
    setVideoDuration,
    audioEnabled,
    setAudioEnabled,
    audioToggleDisabled,
    canUseFrameInputs,
    canUseReferenceInputs,
    canUseReferenceVideoInputs,
    canUseReferenceAudioInputs,
    frameInputs,
    setFrameInputs,
    referenceImages,
    setReferenceImages,
    referenceVideos,
    setReferenceVideos,
    referenceAudios,
    setReferenceAudios,
    supportedVideoSizes,
    supportedVideoDurations,
    isGenerating,
    onGenerate,
    imageInputRequired
  } = t0;
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadTarget, setUploadTarget] = useState("frame-start");
  const [referenceVideoDraft, setReferenceVideoDraft] = useState("");
  let t1;
  if ($[0] !== referenceVideoDraft || $[1] !== setFrameInputs || $[2] !== setReferenceVideos) {
    t1 = () => {
      const url = referenceVideoDraft.trim();
      if (!url || !/^https:\/\//i.test(url)) {
        return;
      }
      setFrameInputs({
        start: null,
        end: null
      });
      setReferenceVideos(prev => prev.length >= 3 || prev.includes(url) ? prev : [...prev, url]);
      setReferenceVideoDraft("");
    };
    $[0] = referenceVideoDraft;
    $[1] = setFrameInputs;
    $[2] = setReferenceVideos;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const addReferenceVideo = t1;
  let t2;
  if ($[4] !== setReferenceVideos) {
    t2 = index => {
      setReferenceVideos(prev_0 => prev_0.filter((_, i) => i !== index));
    };
    $[4] = setReferenceVideos;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const removeReferenceVideo = t2;
  const [referenceAudioDraft, setReferenceAudioDraft] = useState("");
  let t3;
  if ($[6] !== referenceAudioDraft || $[7] !== setFrameInputs || $[8] !== setReferenceAudios) {
    t3 = () => {
      const url_0 = referenceAudioDraft.trim();
      if (!url_0 || !/^https:\/\//i.test(url_0)) {
        return;
      }
      setFrameInputs({
        start: null,
        end: null
      });
      setReferenceAudios(prev_1 => prev_1.length >= 3 || prev_1.includes(url_0) ? prev_1 : [...prev_1, url_0]);
      setReferenceAudioDraft("");
    };
    $[6] = referenceAudioDraft;
    $[7] = setFrameInputs;
    $[8] = setReferenceAudios;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const addReferenceAudio = t3;
  let t4;
  if ($[10] !== setReferenceAudios) {
    t4 = index_0 => {
      setReferenceAudios(prev_2 => prev_2.filter((__0, i_0) => i_0 !== index_0));
    };
    $[10] = setReferenceAudios;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const removeReferenceAudio = t4;
  const hasImageInput = !!frameInputs.start;
  const missingRequiredImage = imageInputRequired && !hasImageInput;
  let t5;
  if ($[12] !== missingRequiredImage || $[13] !== prompt || $[14] !== selectedModels) {
    t5 = prompt.trim().length > 0 && selectedModels.length > 0 && !missingRequiredImage;
    $[12] = missingRequiredImage;
    $[13] = prompt;
    $[14] = selectedModels;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const canGenerate = t5;
  const canAcceptInput = canUseFrameInputs || canUseReferenceInputs;
  const defaultUploadTarget = !canUseReferenceInputs ? frameInputs.start ? "frame-end" : "frame-start" : !canUseFrameInputs || uploadTarget === "reference" || referenceImages.length > 0 ? "reference" : frameInputs.start ? "frame-end" : "frame-start";
  let t6;
  if ($[16] !== canAcceptInput || $[17] !== canUseFrameInputs || $[18] !== canUseReferenceInputs || $[19] !== defaultUploadTarget || $[20] !== setFrameInputs || $[21] !== setReferenceAudios || $[22] !== setReferenceImages || $[23] !== setReferenceVideos) {
    t6 = (file, explicitTarget) => {
      if (!canAcceptInput || !file.type.startsWith("image/")) {
        return;
      }
      const target = explicitTarget ?? defaultUploadTarget;
      if ((target === "frame-start" || target === "frame-end") && !canUseFrameInputs) {
        return;
      }
      if (target === "reference" && !canUseReferenceInputs) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const nextImage = {
          dataUrl: String(reader.result ?? ""),
          mediaType: file.type
        };
        if (target === "frame-start") {
          setReferenceImages([]);
          setReferenceVideos([]);
          setReferenceAudios([]);
          setFrameInputs(prev_3 => ({
            ...prev_3,
            start: nextImage
          }));
          return;
        }
        if (target === "frame-end") {
          setReferenceImages([]);
          setReferenceVideos([]);
          setReferenceAudios([]);
          setFrameInputs(prev_4 => ({
            ...prev_4,
            end: nextImage
          }));
          return;
        }
        setFrameInputs({
          start: null,
          end: null
        });
        setReferenceImages(prev_5 => {
          if (prev_5.length >= 3) {
            return prev_5;
          }
          return [...prev_5, nextImage];
        });
      };
      reader.readAsDataURL(file);
    };
    $[16] = canAcceptInput;
    $[17] = canUseFrameInputs;
    $[18] = canUseReferenceInputs;
    $[19] = defaultUploadTarget;
    $[20] = setFrameInputs;
    $[21] = setReferenceAudios;
    $[22] = setReferenceImages;
    $[23] = setReferenceVideos;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const addImageFile = t6;
  let t7;
  if ($[25] !== canGenerate || $[26] !== isGenerating || $[27] !== onGenerate) {
    t7 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canGenerate && !isGenerating) {
          onGenerate();
        }
      }
    };
    $[25] = canGenerate;
    $[26] = isGenerating;
    $[27] = onGenerate;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const handleKeyDown = t7;
  let t8;
  if ($[29] !== addImageFile || $[30] !== referenceImages.length || $[31] !== uploadTarget) {
    t8 = e_0 => {
      const files = Array.from(e_0.target.files ?? []);
      for (const file_0 of files) {
        if (uploadTarget === "reference" && referenceImages.length >= 3) {
          break;
        }
        addImageFile(file_0, uploadTarget);
        if (uploadTarget !== "reference") {
          break;
        }
      }
      e_0.target.value = "";
    };
    $[29] = addImageFile;
    $[30] = referenceImages.length;
    $[31] = uploadTarget;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const handleFileSelect = t8;
  let t9;
  if ($[33] !== addImageFile || $[34] !== canAcceptInput) {
    t9 = e_1 => {
      if (!canAcceptInput) {
        return;
      }
      const items = Array.from(e_1.clipboardData.items);
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e_1.preventDefault();
          const file_1 = item.getAsFile();
          if (file_1) {
            addImageFile(file_1);
          }
          break;
        }
      }
    };
    $[33] = addImageFile;
    $[34] = canAcceptInput;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const handlePaste = t9;
  let t10;
  if ($[36] !== canAcceptInput) {
    t10 = e_2 => {
      e_2.preventDefault();
      e_2.stopPropagation();
      if (canAcceptInput) {
        setIsDragging(true);
      }
    };
    $[36] = canAcceptInput;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const handleDragOver = t10;
  let t11;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_3 => {
      e_3.preventDefault();
      e_3.stopPropagation();
      if (dropZoneRef.current && !dropZoneRef.current.contains(e_3.relatedTarget as Node)) {
        setIsDragging(false);
      }
    };
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const handleDragLeave = t11;
  let t12;
  if ($[39] !== addImageFile || $[40] !== canAcceptInput || $[41] !== defaultUploadTarget) {
    t12 = e_4 => {
      e_4.preventDefault();
      e_4.stopPropagation();
      setIsDragging(false);
      if (!canAcceptInput) {
        return;
      }
      const files_0 = Array.from(e_4.dataTransfer.files);
      for (const file_2 of files_0) {
        if (file_2.type.startsWith("image/")) {
          addImageFile(file_2, defaultUploadTarget);
          if (defaultUploadTarget !== "reference") {
            break;
          }
        }
      }
    };
    $[39] = addImageFile;
    $[40] = canAcceptInput;
    $[41] = defaultUploadTarget;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const handleDrop = t12;
  let t13;
  let t14;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      const handleWindowDragEnd = () => setIsDragging(false);
      window.addEventListener("dragend", handleWindowDragEnd);
      return () => window.removeEventListener("dragend", handleWindowDragEnd);
    };
    t14 = [];
    $[43] = t13;
    $[44] = t14;
  } else {
    t13 = $[43];
    t14 = $[44];
  }
  useEffect(t13, t14);
  let t15;
  if ($[45] !== setFrameInputs) {
    t15 = target_0 => {
      setFrameInputs(prev_6 => ({
        ...prev_6,
        [target_0]: null
      }));
    };
    $[45] = setFrameInputs;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  const removeFrameImage = t15;
  let t16;
  if ($[47] !== setReferenceImages) {
    t16 = index_1 => {
      setReferenceImages(prev_7 => prev_7.filter((__1, i_1) => i_1 !== index_1));
    };
    $[47] = setReferenceImages;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const removeReferenceImage = t16;
  let t17;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = target_1 => {
      setUploadTarget(target_1);
      fileInputRef.current?.click();
    };
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  const openFilePicker = t17;
  const t18 = `rounded-md border-input border dark:bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring transition-colors ${isDragging ? "border-primary bg-primary/5 ring-1 ring-primary" : ""}`;
  let t19;
  if ($[50] !== canAcceptInput || $[51] !== isDragging) {
    t19 = isDragging && canAcceptInput && <div className="flex items-center justify-center py-4 text-sm text-muted-foreground"><ImagePlus className="mr-2 h-4 w-4" />Drop image here</div>;
    $[50] = canAcceptInput;
    $[51] = isDragging;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== frameInputs.end || $[54] !== frameInputs.start || $[55] !== removeFrameImage) {
    t20 = (frameInputs.start ?? frameInputs.end) && <div className="flex flex-wrap gap-2 px-3 pt-3">{[{
        key: "start" as const,
        label: "First",
        image: frameInputs.start
      }, {
        key: "end" as const,
        label: "Last",
        image: frameInputs.end
      }].map(item_0 => {
        if (!item_0.image) {
          return null;
        }
        return <div key={item_0.key} className="space-y-1"><div className="px-1 text-xs text-muted-foreground">{item_0.label} frame</div><div className="group relative h-14 w-14 rounded-md border"><img src={item_0.image.dataUrl} alt={`${item_0.label} frame`} className="size-full rounded-md object-cover" /><button type="button" aria-label={`Remove ${item_0.label} frame`} onClick={() => removeFrameImage(item_0.key)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div></div>;
      })}</div>;
    $[53] = frameInputs.end;
    $[54] = frameInputs.start;
    $[55] = removeFrameImage;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== referenceImages || $[58] !== removeReferenceImage) {
    t21 = referenceImages.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceImages.map((image, index_2) => <div key={`${image.dataUrl}-${index_2}`} className="space-y-1"><div className="px-1 text-xs text-muted-foreground">Reference {index_2 + 1}</div><div className="group relative h-14 w-14 rounded-md border"><img src={image.dataUrl} alt={`Reference ${index_2 + 1}`} className="size-full rounded-md object-cover" /><button type="button" aria-label="Remove reference image" onClick={() => removeReferenceImage(index_2)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div></div>)}</div>;
    $[57] = referenceImages;
    $[58] = removeReferenceImage;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== referenceVideos || $[61] !== removeReferenceVideo) {
    t22 = referenceVideos.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceVideos.map((url_1, index_3) => <div key={`${url_1}-${index_3}`} className="group relative flex max-w-[220px] items-center gap-1.5 rounded-md border bg-muted/40 py-1 pl-2 pr-6 text-xs" title={url_1}><Film className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">Reference video {index_3 + 1}</span><button type="button" aria-label="Remove reference video" onClick={() => removeReferenceVideo(index_3)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[60] = referenceVideos;
    $[61] = removeReferenceVideo;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== referenceAudios || $[64] !== removeReferenceAudio) {
    t23 = referenceAudios.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceAudios.map((url_2, index_4) => <div key={`${url_2}-${index_4}`} className="group relative flex max-w-[220px] items-center gap-1.5 rounded-md border bg-muted/40 py-1 pl-2 pr-6 text-xs" title={url_2}><Music className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">Reference audio {index_4 + 1}</span><button type="button" aria-label="Remove reference audio" onClick={() => removeReferenceAudio(index_4)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[63] = referenceAudios;
    $[64] = removeReferenceAudio;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== setPrompt) {
    t24 = e_5 => setPrompt(e_5.target.value);
    $[66] = setPrompt;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== handleKeyDown || $[69] !== handlePaste || $[70] !== isGenerating || $[71] !== prompt || $[72] !== t24) {
    t25 = <Textarea ref={textareaRef} value={prompt} onChange={t24} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder="Describe the video you want to generate... (optionally paste or drop a first/last frame or reference image)" className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent" disabled={isGenerating} />;
    $[68] = handleKeyDown;
    $[69] = handlePaste;
    $[70] = isGenerating;
    $[71] = prompt;
    $[72] = t24;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== handleDragOver || $[75] !== handleDrop || $[76] !== handlePaste || $[77] !== t18 || $[78] !== t19 || $[79] !== t20 || $[80] !== t21 || $[81] !== t22 || $[82] !== t23 || $[83] !== t25) {
    t26 = <div ref={dropZoneRef} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onPaste={handlePaste} className={t18}>{t19}{t20}{t21}{t22}{t23}{t25}</div>;
    $[74] = handleDragOver;
    $[75] = handleDrop;
    $[76] = handlePaste;
    $[77] = t18;
    $[78] = t19;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
    $[82] = t23;
    $[83] = t25;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== handleFileSelect) {
    t27 = <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />;
    $[85] = handleFileSelect;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => openFilePicker("frame-start");
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  const t29 = isGenerating || !canUseFrameInputs;
  const t30 = !canUseFrameInputs ? "Frame input not supported by selected model" : undefined;
  let t31;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <ImagePlus className="mr-1.5 h-4 w-4" />;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  const t32 = frameInputs.start ? "Replace first" : "First frame";
  let t33;
  if ($[89] !== t29 || $[90] !== t30 || $[91] !== t32) {
    t33 = <Button variant="outline" size="sm" onClick={t28} disabled={t29} title={t30}>{t31}{t32}</Button>;
    $[89] = t29;
    $[90] = t30;
    $[91] = t32;
    $[92] = t33;
  } else {
    t33 = $[92];
  }
  let t34;
  if ($[93] !== canUseFrameInputs || $[94] !== frameInputs.end || $[95] !== imageInputRequired || $[96] !== isGenerating) {
    t34 = !imageInputRequired && <Button variant="outline" size="sm" onClick={() => openFilePicker("frame-end")} disabled={isGenerating || !canUseFrameInputs} title={!canUseFrameInputs ? "Frame input not supported by selected model" : undefined}><ImagePlus className="mr-1.5 h-4 w-4" />{frameInputs.end ? "Replace last" : "Last frame"}</Button>;
    $[93] = canUseFrameInputs;
    $[94] = frameInputs.end;
    $[95] = imageInputRequired;
    $[96] = isGenerating;
    $[97] = t34;
  } else {
    t34 = $[97];
  }
  let t35;
  if ($[98] !== canUseReferenceInputs || $[99] !== imageInputRequired || $[100] !== isGenerating || $[101] !== referenceImages.length) {
    t35 = !imageInputRequired && <Button variant="outline" size="sm" onClick={() => openFilePicker("reference")} disabled={isGenerating || !canUseReferenceInputs || referenceImages.length >= 3} title={!canUseReferenceInputs ? "Reference images not supported by selected model" : undefined}><ImagePlus className="mr-1.5 h-4 w-4" />{referenceImages.length === 0 ? "Reference" : `${referenceImages.length}/3 refs`}</Button>;
    $[98] = canUseReferenceInputs;
    $[99] = imageInputRequired;
    $[100] = isGenerating;
    $[101] = referenceImages.length;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  let t36;
  if ($[103] !== addReferenceVideo || $[104] !== canUseReferenceVideoInputs || $[105] !== isGenerating || $[106] !== referenceVideoDraft || $[107] !== referenceVideos.length) {
    t36 = canUseReferenceVideoInputs && <div className="flex items-center gap-1.5"><Input value={referenceVideoDraft} onChange={e_6 => setReferenceVideoDraft(e_6.target.value)} onKeyDown={e_7 => {
        if (e_7.key === "Enter") {
          e_7.preventDefault();
          addReferenceVideo();
        }
      }} placeholder="Reference video URL (https://)" disabled={isGenerating || referenceVideos.length >= 3} className="h-8 w-[220px] text-sm" /><Button variant="outline" size="sm" onClick={addReferenceVideo} disabled={isGenerating || referenceVideos.length >= 3 || !/^https:\/\//i.test(referenceVideoDraft.trim())} title="Add an HTTPS reference video URL"><Plus className="mr-1.5 h-4 w-4" />{referenceVideos.length === 0 ? "Video" : `${referenceVideos.length}/3 videos`}</Button></div>;
    $[103] = addReferenceVideo;
    $[104] = canUseReferenceVideoInputs;
    $[105] = isGenerating;
    $[106] = referenceVideoDraft;
    $[107] = referenceVideos.length;
    $[108] = t36;
  } else {
    t36 = $[108];
  }
  let t37;
  if ($[109] !== addReferenceAudio || $[110] !== canUseReferenceAudioInputs || $[111] !== isGenerating || $[112] !== referenceAudioDraft || $[113] !== referenceAudios.length) {
    t37 = canUseReferenceAudioInputs && <div className="flex items-center gap-1.5"><Input value={referenceAudioDraft} onChange={e_8 => setReferenceAudioDraft(e_8.target.value)} onKeyDown={e_9 => {
        if (e_9.key === "Enter") {
          e_9.preventDefault();
          addReferenceAudio();
        }
      }} placeholder="Reference audio URL (https://)" disabled={isGenerating || referenceAudios.length >= 3} className="h-8 w-[220px] text-sm" /><Button variant="outline" size="sm" onClick={addReferenceAudio} disabled={isGenerating || referenceAudios.length >= 3 || !/^https:\/\//i.test(referenceAudioDraft.trim())} title="Add an HTTPS reference audio URL"><Plus className="mr-1.5 h-4 w-4" />{referenceAudios.length === 0 ? "Audio ref" : `${referenceAudios.length}/3 audio`}</Button></div>;
    $[109] = addReferenceAudio;
    $[110] = canUseReferenceAudioInputs;
    $[111] = isGenerating;
    $[112] = referenceAudioDraft;
    $[113] = referenceAudios.length;
    $[114] = t37;
  } else {
    t37 = $[114];
  }
  let t38;
  if ($[115] !== setVideoSize) {
    t38 = val => setVideoSize(val as VideoSize);
    $[115] = setVideoSize;
    $[116] = t38;
  } else {
    t38 = $[116];
  }
  let t39;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <SelectTrigger size="sm" className="min-w-[160px]"><SelectValue placeholder="Resolution" /></SelectTrigger>;
    $[117] = t39;
  } else {
    t39 = $[117];
  }
  let t40;
  if ($[118] !== supportedVideoSizes) {
    t40 = getVideoSizes().map(size => <SelectItem key={size} value={size} disabled={!supportedVideoSizes.includes(size)}>{getVideoSizeLabel(size)}</SelectItem>);
    $[118] = supportedVideoSizes;
    $[119] = t40;
  } else {
    t40 = $[119];
  }
  let t41;
  if ($[120] !== t40) {
    t41 = <SelectContent>{t40}</SelectContent>;
    $[120] = t40;
    $[121] = t41;
  } else {
    t41 = $[121];
  }
  let t42;
  if ($[122] !== t38 || $[123] !== t41 || $[124] !== videoSize) {
    t42 = <Select value={videoSize} onValueChange={t38}>{t39}{t41}</Select>;
    $[122] = t38;
    $[123] = t41;
    $[124] = videoSize;
    $[125] = t42;
  } else {
    t42 = $[125];
  }
  const t43 = String(videoDuration);
  let t44;
  if ($[126] !== setVideoDuration) {
    t44 = val_0 => setVideoDuration(Number(val_0) as VideoDuration);
    $[126] = setVideoDuration;
    $[127] = t44;
  } else {
    t44 = $[127];
  }
  let t45;
  if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <SelectTrigger size="sm" className="min-w-[100px]"><SelectValue placeholder="Duration" /></SelectTrigger>;
    $[128] = t45;
  } else {
    t45 = $[128];
  }
  let t46;
  if ($[129] !== supportedVideoDurations) {
    t46 = getVideoDurations().map(duration => <SelectItem key={duration} value={String(duration)} disabled={!supportedVideoDurations.includes(duration)}>{duration} seconds</SelectItem>);
    $[129] = supportedVideoDurations;
    $[130] = t46;
  } else {
    t46 = $[130];
  }
  let t47;
  if ($[131] !== t46) {
    t47 = <SelectContent>{t46}</SelectContent>;
    $[131] = t46;
    $[132] = t47;
  } else {
    t47 = $[132];
  }
  let t48;
  if ($[133] !== t43 || $[134] !== t44 || $[135] !== t47) {
    t48 = <Select value={t43} onValueChange={t44}>{t45}{t47}</Select>;
    $[133] = t43;
    $[134] = t44;
    $[135] = t47;
    $[136] = t48;
  } else {
    t48 = $[136];
  }
  const t49 = audioToggleDisabled && !isGenerating ? "Silent output is only available on Google Vertex" : undefined;
  let t50;
  if ($[137] !== audioEnabled || $[138] !== audioToggleDisabled || $[139] !== setAudioEnabled) {
    t50 = <Switch checked={audioEnabled} onCheckedChange={setAudioEnabled} disabled={audioToggleDisabled} />;
    $[137] = audioEnabled;
    $[138] = audioToggleDisabled;
    $[139] = setAudioEnabled;
    $[140] = t50;
  } else {
    t50 = $[140];
  }
  let t51;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <span>Audio</span>;
    $[141] = t51;
  } else {
    t51 = $[141];
  }
  let t52;
  if ($[142] !== t49 || $[143] !== t50) {
    t52 = <label className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm" title={t49}>{t50}{t51}</label>;
    $[142] = t49;
    $[143] = t50;
    $[144] = t52;
  } else {
    t52 = $[144];
  }
  let t53;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <div className="flex-1" />;
    $[145] = t53;
  } else {
    t53 = $[145];
  }
  let t54;
  if ($[146] !== missingRequiredImage) {
    t54 = missingRequiredImage && <p className="text-sm text-destructive">This model requires a start frame image</p>;
    $[146] = missingRequiredImage;
    $[147] = t54;
  } else {
    t54 = $[147];
  }
  const t55 = isGenerating || !canGenerate;
  let t56;
  if ($[148] !== isGenerating) {
    t56 = isGenerating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : <><Sparkles className="mr-2 h-4 w-4" />Generate</>;
    $[148] = isGenerating;
    $[149] = t56;
  } else {
    t56 = $[149];
  }
  let t57;
  if ($[150] !== onGenerate || $[151] !== t55 || $[152] !== t56) {
    t57 = <Button onClick={onGenerate} disabled={t55} className="min-w-[120px]">{t56}</Button>;
    $[150] = onGenerate;
    $[151] = t55;
    $[152] = t56;
    $[153] = t57;
  } else {
    t57 = $[153];
  }
  let t58;
  if ($[154] !== t33 || $[155] !== t34 || $[156] !== t35 || $[157] !== t36 || $[158] !== t37 || $[159] !== t42 || $[160] !== t48 || $[161] !== t52 || $[162] !== t54 || $[163] !== t57) {
    t58 = <div className="flex flex-wrap items-center gap-2">{t33}{t34}{t35}{t36}{t37}{t42}{t48}{t52}{t53}{t54}{t57}</div>;
    $[154] = t33;
    $[155] = t34;
    $[156] = t35;
    $[157] = t36;
    $[158] = t37;
    $[159] = t42;
    $[160] = t48;
    $[161] = t52;
    $[162] = t54;
    $[163] = t57;
    $[164] = t58;
  } else {
    t58 = $[164];
  }
  let t59;
  if ($[165] !== t26 || $[166] !== t27 || $[167] !== t58) {
    t59 = <div className="border-b bg-background p-4"><div className="max-w-4xl mx-auto space-y-3">{t26}{t27}{t58}</div></div>;
    $[165] = t26;
    $[166] = t27;
    $[167] = t58;
    $[168] = t59;
  } else {
    t59 = $[168];
  }
  return t59;
}